import { useMemo, useEffect, useCallback, useState, useRef } from "react";
import { Stack, TooltipHost, TooltipDelay, DirectionalHint } from "@fluentui/react";

import styles from "./Answer.module.css";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import "tippy.js/animations/shift-away.css";
import mermaid from "mermaid";

import { FiFileText, FiRotateCw, FiChevronLeft, FiChevronRight, FiCopy } from "react-icons/fi";
import { BsDatabase } from "react-icons/bs";
import { FaQuestionCircle, FaPauseCircle, FaPlayCircle } from "react-icons/fa";

import { AnswerIcon } from "./AnswerIcon";
import { parseAnswerToHtml } from "./AnswerParser";
import { PiChatCircleTextBold } from "react-icons/pi";
import ChartRenderer from "./../ChartRenderer/ChartRenderer";
import FeedbackComponent from "../FeedbackComponent/FeedbackComponent";
import SimpleSQLFormatter from "./SimpleSQLFormatter";
import { FaRegCopy } from "react-icons/fa6";
import { ChatAppResponse } from "../../api";
import { gselectedgroup, gfeedback, gcaption, gspeakprops, DataPoint, gfileuserselect } from "../../interfaces";
import {
    convertMarkdownHeadersToHTML,
    copyToClipboard,
    extractTextCptns,
    isTouchDevice,
    genTbl,
    safeJsonParse,
    jsonToKeyValueCsv,
    isEmptyObject,
    jsonToKeyValueCsv_v2,
    formatSQL
} from "../../util_glob";
import { prep_next_turn_collection_vals } from "../../components/SpeechRecognizer/recog_util";
import { assistantCodes } from "../../lsts";
import SimpleSQLFormatter_v2 from "./SimpleSQLFormatter";

interface CustomElement extends HTMLElement {
    listenerAdded?: boolean;
}

let mermaidInitialized = false;
const ensureMermaidInit = () => {
    if (mermaidInitialized) return;
    try {
        mermaid.initialize({ startOnLoad: false, securityLevel: "loose", theme: "default" });
        mermaidInitialized = true;
    } catch (e) {
        console.error("errr‾mermaid_init", e);
    }
};

const decodeMermaidSrc = (encoded: string): string => {
    try {
        return decodeURIComponent(escape(atob(encoded)));
    } catch {
        return "";
    }
};

const normalizeMermaidSyntax = (raw: string): string => {
    let src = (raw || "").trim();

    // unwrap fenced blocks if the model included them anyway
    if (src.startsWith("```")) {
        src = src
            .replace(/^```\s*mermaid\s*/i, "")
            .replace(/^```\s*/i, "")
            .replace(/```\s*$/i, "")
            .trim();
    }

    // Auto-fix common invalid xychart form:
    // bar
    //   "Label": 10
    // into:
    // x-axis ["Label"]
    // bar [10]
    if (!/^\s*xychart-beta\b/m.test(src)) return src;

    const lines = src.split(/\r?\n/);
    const barIdx = lines.findIndex(l => /^\s*bar\s*$/.test(l));
    if (barIdx < 0) return src;

    const kvStart = barIdx + 1;
    let kvEnd = kvStart;
    const labels: string[] = [];
    const values: string[] = [];

    for (let i = kvStart; i < lines.length; i++) {
        const line = lines[i];
        if (!line.trim()) {
            kvEnd = i + 1;
            continue;
        }

        const m = line.match(/^\s*["']?(.+?)["']?\s*:\s*(-?\d+(?:\.\d+)?)\s*,?\s*$/);
        if (!m) break;

        labels.push(m[1].replace(/"/g, '\\"'));
        values.push(m[2]);
        kvEnd = i + 1;
    }

    if (labels.length === 0) return src;

    // replace or insert x-axis categories
    const xAxisIdx = lines.findIndex(l => /^\s*x-axis\b/.test(l));
    const xAxisLine = `    x-axis [${labels.map(l => `"${l}"`).join(", ")}]`;
    if (xAxisIdx >= 0) {
        lines[xAxisIdx] = xAxisLine;
    } else {
        lines.splice(Math.max(1, barIdx), 0, xAxisLine);
    }

    // replace invalid bar block with valid value array
    lines.splice(barIdx, Math.max(1, kvEnd - barIdx), `    bar [${values.join(", ")}]`);

    return lines.join("\n");
};

/** Detect whether a raw chart string is Mermaid syntax (vs ChartJS JSON). */
const isMermaidSyntax = (s: string): boolean => {
    const t = s.trim();
    return /^(pie|xychart-beta|flowchart|graph\s|graph\n|sequenceDiagram|gantt|mindmap|classDiagram|stateDiagram|erDiagram|journey|gitgraph|timeline|sankey-beta|quadrantChart|block-beta|packet-beta|kanban|architecture-beta|C4Context|C4Container|C4Component|C4Deployment|requirementDiagram|zenuml)/i.test(t);
};

const renderMermaidBlocks = async (container: HTMLElement) => {
    const nodes = container.querySelectorAll<HTMLElement>(".mermaid-block:not([data-mermaid-rendered])");
    if (!nodes.length) return;
    ensureMermaidInit();
    let idx = 0;
    for (const node of Array.from(nodes)) {
        const src = normalizeMermaidSyntax(decodeMermaidSrc(node.getAttribute("data-mermaid-src") || ""));
        node.setAttribute("data-mermaid-rendered", "1");
        if (!src) continue;
        const renderId = `mermaid-svg-${Date.now()}-${idx++}-${Math.random().toString(36).slice(2, 8)}`;
        try {
            const { svg, bindFunctions } = await mermaid.render(renderId, src);
            node.innerHTML = svg;
            const svgEl = node.querySelector("svg") as SVGElement | null;
            if (svgEl) {
                svgEl.style.width = "100%";
                svgEl.style.maxWidth = "100%";
                svgEl.style.height = "auto";
                svgEl.style.display = "block";
            }
            if (bindFunctions) bindFunctions(node);
        } catch (e) {
            console.error("errr‾mermaid_render", e);
            node.innerHTML = `<pre class="mermaid-error">${src.replace(/[<>&]/g, c => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c] || c))}</pre>`;
        }
    }
};

interface Props {
    index: number;
    answer: ChatAppResponse;
    isLoading?: boolean;
    isStreaming: boolean;
    speakStatus: gspeakprops;
    selectedGroup: gselectedgroup;
    showFollowupQuestions?: boolean;
    clickedCitation: string;
    totalAnswers: number;
    isSBS?: boolean;
    isReverseAnswerList?: boolean;
    activeConvoKey?: string;
    onCitationClicked: (filePath: string, integratedCitation: string | null, el: HTMLElement | null, openSBS?: boolean) => void;
    onFollowupQuestionClicked?: (question: string) => void;
    onSendFeedback: (prps: gfeedback) => void;
    manageSpeechPause: (val: boolean) => void;
    sendToSupport?: (question: string) => void;
    onChartRegenClick?: (answer: ChatAppResponse, answerIndex: number, anchorEl: HTMLDivElement | null) => void;
}

export const Answer = ({
    index,
    answer,
    isLoading,
    isStreaming,
    speakStatus,
    selectedGroup,
    showFollowupQuestions,
    clickedCitation,
    totalAnswers,
    isSBS,
    isReverseAnswerList,
    activeConvoKey,
    onSendFeedback,
    onCitationClicked,
    onFollowupQuestionClicked,
    manageSpeechPause,
    sendToSupport,
    onChartRegenClick
}: Props) => {
    // chart navigation state - keyed by selectionId + activeConvoKey + index
    const chartIndexKey = `${selectedGroup.selectionId}_${activeConvoKey || ''}_${index}`;
    const [chartIndices, setChartIndices] = useState<Record<string, number>>({});
    const activeChartIndex = chartIndices[chartIndexKey] ?? 0;
    const chartRegenRef = useRef<HTMLDivElement>(null);

    const followupQuestions = answer.choices[0].context.followup_questions;
    const messageContent = answer.choices[0].message.content;
    const citationData = answer.choices[0].context.data_points_integrated?.citations || [];
    const parsedAnswer = useMemo(
        () => parseAnswerToHtml(messageContent, isStreaming, citationData, index, clickedCitation, selectedGroup.assistanttype),
        [answer]
    );
    const cca_exmpls = useMemo(() => {
        if (answer.expected_next_user_response_type && index == totalAnswers - 1) {
            return prep_next_turn_collection_vals(selectedGroup, answer.expected_next_user_response_type);
        } else {
            return null;
        }
    }, [answer.expected_next_user_response_type]);

    const q_data = useMemo(() => {
            return ((answer.choices[0]?.context?.sql_query) ? SimpleSQLFormatter_v2(answer?.choices[0]?.context?.sql_query || '') : ((answer.choices[0]?.context?.collected_data) ? safeJsonParse(answer.choices[0].context.collected_data) : null))
    },[answer]);

    const qFidSel = useMemo(() => {
        return answer?.fidselect ?? [];
    },[answer]);

    const q_data_for_paste = useMemo(() => {
            return ((answer.choices[0]?.context?.sql_query) ? formatSQL(answer?.choices[0]?.context?.sql_query || '') : ((answer.choices[0]?.context?.collected_data) ? safeJsonParse(answer.choices[0].context.collected_data) : null))
    },[answer]);

    const dataPoints = useMemo(() => {
        let points: gcaption[] = [];
        let citD = answer.choices[0].context.data_points_integrated?.citations || [];
        citD?.map((x: any) => {
            points.push({
                content: x.content,
                filename: x.filepath.split("#page=")[0],
                page: `${x.filepath.split("#page=")[1]}`,
                fileid: x.url,
                chunkid: x.chunk_id
            });
        });
        const uniqueDataPoints: { [key: string]: DataPoint } = {};
        points = points.sort((a, b) => (a.chunkid || 0) - (b.chunkid || 0));
        points.forEach(point => {
            if (uniqueDataPoints[point.filename]) {
                uniqueDataPoints[point.filename].pages.push(point);
            } else {
                uniqueDataPoints[point.filename] = { content: point.content, filename: point.filename, pages: [point], fileid: point.fileid };
            }
        });
        return Object.values(uniqueDataPoints);
    }, [answer.choices[0].context.data_points, answer.choices[0].context.data_points_integrated]);

    // Normalize chartjs_json to a single ordered list; each item is either ChartJS config or Mermaid syntax.
    const allCharts = useMemo(() => {
        const out: Array<{ kind: 'chartjs' | 'mermaid'; value: any }> = [];
        const pushChartItem = (item: any) => {
            if (typeof item === 'string') {
                const t = item.trim();
                if (!t) return;
                if (isMermaidSyntax(t)) {
                    out.push({ kind: 'mermaid', value: t });
                    return;
                }
                const parsed = safeJsonParse(t);
                if (Array.isArray(parsed)) {
                    parsed.forEach(pushChartItem);
                    return;
                }
                if (parsed && typeof parsed === 'object') {
                    out.push({ kind: 'chartjs', value: parsed });
                    return;
                }
                return;
            }

            if (Array.isArray(item)) {
                item.forEach(pushChartItem);
                return;
            }

            if (item && typeof item === 'object') {
                out.push({ kind: 'chartjs', value: item });
            }
        };

        try {
            pushChartItem(answer.choices[0].context.chartjs_json);
        } catch (e) {
            import.meta.env.DEV === true && console.log('‹errr_chart_parse›', e);
        }

        return out;
    }, [answer.choices[0].context.chartjs_json]);

    const chart = useMemo(() => {
        try {
            if (allCharts.length === 0) return null;

            // clamp activeChartIndex to valid range
            const chartIndex = Math.max(0, Math.min(activeChartIndex, allCharts.length - 1));
            const selected = allCharts[chartIndex];

            if (selected.kind === 'mermaid') {
                const encoded = btoa(unescape(encodeURIComponent(selected.value)));
                const html = `<div class="mermaid-block" data-mermaid-src="${encoded}"></div>`;
                return <div className={styles.answerText} dangerouslySetInnerHTML={{ __html: html }}></div>;
            }

            return <ChartRenderer configJson={JSON.stringify(selected.value)}/>;
        } catch (e) {
            import.meta.env.DEV === true && console.log('‹errr_chart_render›', e);
            return null;
        }
    }, [allCharts, activeChartIndex]);

    const handleChartNav = useCallback((direction: 'prev' | 'next') => {
        setChartIndices(prev => {
            const currentIndex = prev[chartIndexKey] ?? 0;
            const newIndex = direction === 'prev'
                ? Math.max(0, currentIndex - 1)
                : Math.min(allCharts.length - 1, currentIndex + 1);
            return { ...prev, [chartIndexKey]: newIndex };
        });
    }, [chartIndexKey, allCharts.length]);

    const handleClick = useCallback(
        (event: MouseEvent) => {
            const openCitationFromMessage = (el: HTMLElement, openSBS?: boolean) => {
                const citationPath = el.getAttribute("data-citation-link");
                const integratedCitation = el.getAttribute("data-integrated-citation");

                if (citationPath) onCitationClicked(citationPath, integratedCitation, el, openSBS);
                const tooltip = document.getElementById("tooltip");
                if (tooltip) {
                    tooltip.classList.remove("visible");
                    setTimeout(() => {
                        tooltip.remove();
                    }, 100);
                }
            };

            const target = event.target as HTMLElement;
            if (target.matches(".citation-link")) {
                openCitationFromMessage(target);
            } else if (target.matches(".citation-tooltip-link")) {
                const relevantElement = target.parentElement?.parentElement?.parentElement?.parentElement?.parentElement?.querySelector("sup") as HTMLElement;
                openCitationFromMessage(relevantElement);
            } else if (target.matches("span")) {
                if (target.innerText?.toLowerCase() == "see in document") {
                    const relevantElement = target.parentElement?.parentElement?.parentElement?.parentElement?.parentElement?.parentElement?.querySelector(
                        "sup"
                    ) as HTMLElement;
                    openCitationFromMessage(relevantElement, false);
                } else if (target.innerText?.toLowerCase() == "explore in side-by-side view") {
                    const relevantElement = target.parentElement?.parentElement?.parentElement?.parentElement?.parentElement?.parentElement?.querySelector(
                        "sup"
                    ) as HTMLElement;
                    import.meta.env.DEV && console.log("‹stopWigglePixel_answer›", window.globalWigglePixel);
                    window.globalWigglePixel = 0;
                    openCitationFromMessage(relevantElement, true);
                }
            }
        },
        [onCitationClicked]
    );

    useEffect(() => {
        try{
            if (isStreaming) return;
            const container = document.getElementById(`answr_cntnt_${index}`) as CustomElement;
            if (container && !container.listenerAdded) {
                container.listenerAdded = true;
                container.addEventListener("click", handleClick);
                tippy("[data-tippy-content]", {
                    placement: "auto",
                    arrow: true,
                    animation: "shift-away",
                    theme: "custom",
                    allowHTML: true,
                    interactive: true,
                    duration: [300, 250],
                    offset: [0, 10]
                });
            }
            if (container) {
                renderMermaidBlocks(container);
            }
            // render any mermaid chart blocks in the full answer container
            const answerRoot = document.getElementById(`answr_${index}`) as HTMLElement | null;
            if (answerRoot) {
                renderMermaidBlocks(answerRoot);
            }
        } catch (e) {
            console.error("‹errr‾answu›", e);
        }

    }, [isStreaming, chart, activeChartIndex]);

    return (
        <Stack id={`answr_${index}`} className={`${styles.answerContainer}`} verticalAlign="space-between">
            <Stack.Item>
                {answer.expected_next_user_response_type && (
                    <Stack.Item className={styles.answerLabelContainer} align="center">
                        <div className={styles.answerLabel}>{answer.expected_next_user_response_type}</div>
                        {cca_exmpls && cca_exmpls.length > 0 && (
                            <div className={styles.responseExamplesLabel}>
                                {"Examples"}
                                <div className={styles.exampleValsFromLabel}>
                                    {window.global_convo_extr_conf?.get(answer.expected_next_user_response_type)?.map((val, idx) => (
                                        <div className={styles.exampleValFromLabel} key={idx}>
                                            {val}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </Stack.Item>
                )}
            </Stack.Item>

            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between" className={styles.topHorizontalStack}>
                    <Stack.Item>
                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                            <div onClick={e => {copyToClipboard(JSON.stringify(answer, null, 2));}}>
                                <AnswerIcon />
                            </div>
                            {!isStreaming && (
                                <TooltipHost content="Copy message" directionalHint={DirectionalHint.topCenter}>
                                    <div className={styles.copyAnswerBtn} onClick={() => copyToClipboard(messageContent)}>
                                        <FaRegCopy size={16} />
                                    </div>
                                </TooltipHost>
                            )}
                        </Stack>
                    </Stack.Item>

                    <Stack.Item className={styles.topRightAnswerWrap} shrink={1}>
                        {!isStreaming && q_data && !isEmptyObject(q_data) && (
                            <div className={`${index == totalAnswers - 1 ? styles.answerInfoRight : styles.answerInfoTotallyRight}`}>
                                <div
                                    onClick={e => {
                                        if (answer.choices[0]?.context?.sql_query) {
                                            import.meta.env.DEV && console.log("‹copying_to_clipboard›", q_data_for_paste);
                                            copyToClipboard(q_data_for_paste || "")
                                        } else {
                                            copyToClipboard(jsonToKeyValueCsv(q_data_for_paste || ""));
                                        }
                                    }}
                                    className={styles.sqlQueryLabel}
                                >
                                    <TooltipHost
                                        closeDelay={500}
                                        content={
                                            <div>
                                                <div className={styles.sqltooltip}>
                                                    {answer.choices[0]?.context?.sql_query ? (
                                                        SimpleSQLFormatter(answer?.choices[0]?.context?.sql_query || "")
                                                    ) : answer.choices[0]?.context?.collected_data ? (
                                                        <div dangerouslySetInnerHTML={{ __html: genTbl(answer.choices[0].context.collected_data) }} />
                                                    ) : null}
                                                </div>
                                            </div>
                                        }
                                        directionalHint={DirectionalHint.topCenter}
                                        delay={isTouchDevice() ? TooltipDelay.long : TooltipDelay.zero}
                                    >
                                        <PiChatCircleTextBold size={20}></PiChatCircleTextBold>
                                    </TooltipHost>
                                </div>
                            </div>
                        )}

                        {index == totalAnswers - 1 ? (
                            <div className={styles.feedbackContainerPlayPause}>
                                {speakStatus.isSpeaking == true || speakStatus.isPaused == true ? (
                                    speakStatus.isPaused == false || speakStatus.isPaused == null ? (
                                        <div onClick={() => manageSpeechPause(true)} className={styles.speechControls}>
                                            <FaPauseCircle size={20} />
                                        </div>
                                    ) : (
                                        <div onClick={() => manageSpeechPause(false)} className={styles.speechControls}>
                                            <FaPlayCircle size={20} />
                                        </div>
                                    )
                                ) : null}
                            </div>
                        ) : null}

                        {!isStreaming && index == totalAnswers - 1 ? (
                            <div className={styles.feedbackContainer}>
                                <FeedbackComponent onSendFeedback={onSendFeedback} interaction_guid={answer.interaction_guid} pos={answer?.pos} />
                            </div>
                        ) : isStreaming && isLoading == false && index == totalAnswers - 1 ? (
                            <div className={styles.streamingLabel}>
                                <div className={styles.loader}>
                                    <div className={styles.duo + " " + styles.duo1}>
                                        <div className={styles.dot + " " + styles.dota}></div>
                                        <div className={styles.dot + " " + styles.dotb}></div>
                                    </div>
                                    <div className={styles.duo + " " + styles.duo2}>
                                        <div className={styles.dot + " " + styles.dota}></div>
                                        <div className={styles.dot + " " + styles.dotb}></div>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </Stack.Item>
                </Stack>
            </Stack.Item>

            {!!dataPoints.length && selectedGroup.inscope === 1 && selectedGroup.hideaisearch != 1 && (
                <Stack.Item>
                    <div className={styles.searchResultsLabel}>
                        <div>AI Search Results:</div>
                        <div className={styles.askMoreIcon}>
                            <TooltipHost content="All Content considered by the Assistant to answer you query. The answer might not use all of these sources." directionalHint={DirectionalHint.topCenter}>
                                <FaQuestionCircle size={10}></FaQuestionCircle>
                            </TooltipHost>
                        </div>
                    </div>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        {dataPoints.map((x, i) => (
                            <Stack.Item key={`aisrchres_${x.fileid || "nofleid"}_${i}_${index}`} className={styles.sourceDoc}>
                                <div className={styles.citationIcon}>
                                    <FiFileText size={16} />
                                </div>
                                <div className={styles.citationFileText}>{`${x.filename}`}</div>

                                {x.pages.length > 0 ? (
                                    x.pages.map((page, subindex) => {
                                        const title = `/content/${x.filename}#page=${page.page}_fid=${x.fileid}`;
                                        return (
                                            <a key={`${"airesitm"}_${x.fileid}${subindex}`}
                                                onClick={e => {
                                                    onCitationClicked(title, null, null);
                                                    e.stopPropagation();
                                                }}
                                            >
                                                <TooltipHost
                                                    className={styles.srchToolDocTooltip}
                                                    content={
                                                        <div className={styles.srchToolMain}>
                                                            <div className={styles.srchToolDocText}>
                                                                <div className={styles.srchToolDocTitle}>{x.filename}</div>
                                                                <div className={styles.srchToolDocDetails}>
                                                                    <div className={styles.srchToolDocPage}> Page Range {page.page.replace("|", "..")}</div>
                                                                    <div className={styles.srchToolDivider}>·</div>
                                                                    <div>Chunk ID {page.chunkid}</div>
                                                                </div>
                                                            </div>
                                                            <div>{page.content}</div>
                                                        </div>
                                                    }
                                                    directionalHint={DirectionalHint.topCenter}
                                                    delay={isTouchDevice() ? TooltipDelay.long : TooltipDelay.zero}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        return;
                                                    }}
                                                >
                                                    <div className={styles.docSourceCitation} key={`${"airessub"}_${i}_${subindex}`}>
                                                        {page.page.replace("|", "..")}
                                                    </div>
                                                </TooltipHost>
                                            </a>
                                        );
                                    })
                                ) : (
                                    <div/>
                                )}
                            </Stack.Item>
                        ))}
                    </Stack>
                </Stack.Item>
            )}

            <Stack.Item grow>
                <div id={`answr_cntnt_${index}`} className={styles.answerText} dangerouslySetInnerHTML={{ __html: parsedAnswer.answerHtml }}></div>
            </Stack.Item>

            {answer.choices[0]?.context?.no_info_message && (
                <Stack.Item className={styles.externalInfoStackItem}>
                    <div className={styles.searchResultsLabel}>
                        <div className={styles.citationLearnMore}>External Info & Clarifications:</div>
                        <div className={styles.externalInfoMoreIcon}>
                            <TooltipHost content="Any content under External Info & Clarifications is based on general AI knowlege." directionalHint={DirectionalHint.topCenter}>
                                <FaQuestionCircle size={10}/>
                            </TooltipHost>
                        </div>
                    </div>
                    <div className={styles.noInfoMessage}>
                        <div className={styles.answerText} dangerouslySetInnerHTML={{ __html: convertMarkdownHeadersToHTML(answer.choices[0].context.no_info_message) }}/>
                    </div>
                </Stack.Item>
            )}

            {!!parsedAnswer.citations.length && !selectedGroup.disablecitationhighlighting && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }} className={styles.citationStack}>
                        <div className={styles.citationsLabel}>
                            <div className={styles.citationLearnMore}>Citations:</div>
                            <div className={styles.askMoreIconCitations}>
                                <TooltipHost
                                    content="Content used by the Assistant to anwser your query. Click to navigate to the Page of the document. For more precise sourcing use inline citations"
                                    directionalHint={DirectionalHint.topCenter}
                                >
                                    <FaQuestionCircle size={10}/>
                                </TooltipHost>
                            </div>
                        </div>
                        {parsedAnswer.citations.map((x, i) => {
                            const path = `/content/${x}`;
                            const citInf = extractTextCptns(x);
                            return (
                                <a
                                    key={i}
                                    id={`cit_answid_${index}_citinx_${i}`}
                                    className={isSBS == true ? styles.citationQSBS : styles.citationQ}
                                    title={x}
                                    onClick={() => onCitationClicked(path, null, null)}
                                >
                                    <div className={styles.citationIcon}>
                                        <FiFileText size={16} />
                                    </div>
                                    {`${++i}. ${citInf?.filename} [${citInf?.page}]`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
            {answer.active_scenario_id == "no data" && (
                <Stack.Item className={styles.externalInfoStackItem}>
                    <Stack horizontal wrap tokens={{ childrenGap: 6 }}>
                        <a key={'send_mail'} className={styles.followupQuestion} title={'Send Mail'} onClick={() => {sendToSupport ? sendToSupport(answer?.user_q || "") : null}}>
                            Send Question to Support
                        </a>
                    </Stack>
                    <div style={{ marginTop: 10 }}/>
                </Stack.Item>
            )}
            {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && index == totalAnswers - 1 && (
                <Stack.Item className={styles.externalInfoStackItem}>
                    {/* eslint-disable-next-line */}
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ''}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>Follow-up questions:</span>
                        {followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
            {chart && (
                <div className={styles.answerChartContainer}>
                    <div className={styles.chartControls}>
                        {allCharts.length > 1 && (
                            <div className={styles.chartNavButtons}>
                                <button className={styles.chartNavBtn} onClick={() => handleChartNav('prev')} disabled={activeChartIndex === 0} aria-label="Previous chart">
                                    <FiChevronLeft size={16} />
                                </button>
                                <span className={styles.chartCounter}>
                                    {activeChartIndex + 1} / {allCharts.length}
                                </span>
                                <button className={styles.chartNavBtn} onClick={() => handleChartNav('next')} disabled={activeChartIndex >= allCharts.length - 1} aria-label="Next chart">
                                    <FiChevronRight size={16} />
                                </button>
                            </div>
                        )}
                        {onChartRegenClick && (
                            <div ref={chartRegenRef} className={styles.chartRegenBtn} onClick={() => onChartRegenClick(answer, index, chartRegenRef.current)}title="Regenerate chart">
                                <FiRotateCw size={14} />
                            </div>
                        )}
                    </div>
                    {chart}
                </div>
            )}
        </Stack>
    );
};
